/**
 * Self-hosted Analytics SDK for FINRA Compliance
 * All data stays in your Supabase instance
 */

import { supabase } from '@/integrations/supabase/client';

// Generate a unique anonymous ID for users before they sign in
function getAnonymousId(): string {
  const key = 'outlier_anonymous_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

// Generate a session ID (new session after 30 min of inactivity)
function getSessionId(): string {
  const key = 'outlier_session_id';
  const timestampKey = 'outlier_session_timestamp';
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  const now = Date.now();
  const lastActivity = parseInt(localStorage.getItem(timestampKey) || '0', 10);
  let sessionId = localStorage.getItem(key);

  // Start new session if expired or doesn't exist
  if (!sessionId || now - lastActivity > SESSION_TIMEOUT) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(key, sessionId);
  }

  // Update last activity
  localStorage.setItem(timestampKey, now.toString());

  return sessionId;
}

// Detect device type
function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

export interface AnalyticsEvent {
  event_name: string;
  event_category?: string;
  properties?: Record<string, unknown>;
  page_url?: string;
  page_title?: string;
  page_path?: string;
}

export interface AnalyticsContext {
  userId?: string;
  anonymousId: string;
  sessionId: string;
}

let analyticsContext: AnalyticsContext | null = null;
let eventQueue: AnalyticsEvent[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

// Initialize analytics context
export function initAnalytics(userId?: string): AnalyticsContext {
  analyticsContext = {
    userId,
    anonymousId: getAnonymousId(),
    sessionId: getSessionId(),
  };
  return analyticsContext;
}

// Update user ID when they sign in
export function identifyUser(userId: string) {
  if (analyticsContext) {
    analyticsContext.userId = userId;
  } else {
    initAnalytics(userId);
  }

  // Track the identification event
  trackEvent('user_identified', 'auth', { userId });
}

// Clear user on sign out
export function resetAnalytics() {
  if (analyticsContext) {
    analyticsContext.userId = undefined;
  }
}

// Queue event for batched sending
function queueEvent(event: AnalyticsEvent) {
  eventQueue.push(event);

  // Flush after 2 seconds of inactivity or when queue reaches 10 events
  if (flushTimeout) clearTimeout(flushTimeout);

  if (eventQueue.length >= 10) {
    flushEvents();
  } else {
    flushTimeout = setTimeout(flushEvents, 2000);
  }
}

// Send queued events to Supabase
async function flushEvents() {
  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = [];

  if (!analyticsContext) {
    initAnalytics();
  }

  const ctx = analyticsContext!;

  const records = eventsToSend.map(event => ({
    user_id: ctx.userId || null,
    anonymous_id: ctx.anonymousId,
    session_id: ctx.sessionId,
    event_name: event.event_name,
    event_category: event.event_category || null,
    properties: event.properties || {},
    page_url: event.page_url || window.location.href,
    page_title: event.page_title || document.title,
    page_path: event.page_path || window.location.pathname,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent,
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    device_type: getDeviceType(),
    client_timestamp: new Date().toISOString(),
  }));

  try {
    // Use type assertion for analytics_events table (types not yet regenerated)
    const { error } = await (supabase as any)
      .from('analytics_events')
      .insert(records);

    if (error) {
      console.warn('[Analytics] Failed to send events:', error.message);
      // Re-queue failed events (but limit to prevent infinite loops)
      if (eventQueue.length < 50) {
        eventQueue.push(...eventsToSend);
      }
    }
  } catch (err) {
    console.warn('[Analytics] Error sending events:', err);
  }
}

// Main tracking function
export function trackEvent(
  eventName: string,
  category?: string,
  properties?: Record<string, unknown>
) {
  queueEvent({
    event_name: eventName,
    event_category: category,
    properties,
  });
}

// Track page views
export function trackPageView(path?: string, title?: string) {
  // Update session ID on each page view
  if (analyticsContext) {
    analyticsContext.sessionId = getSessionId();
  }

  queueEvent({
    event_name: 'page_view',
    event_category: 'navigation',
    page_path: path || window.location.pathname,
    page_title: title || document.title,
    page_url: window.location.href,
  });
}

// Track feature usage
export function trackFeature(featureName: string, properties?: Record<string, unknown>) {
  trackEvent('feature_used', 'engagement', {
    feature: featureName,
    ...properties,
  });
}

// Track button/element clicks
export function trackClick(elementName: string, properties?: Record<string, unknown>) {
  trackEvent('element_click', 'interaction', {
    element: elementName,
    ...properties,
  });
}

// Track errors
export function trackError(errorName: string, errorMessage?: string, properties?: Record<string, unknown>) {
  trackEvent('error', 'error', {
    error_name: errorName,
    error_message: errorMessage,
    ...properties,
  });
}

// Track conversions
export function trackConversion(conversionName: string, value?: number, properties?: Record<string, unknown>) {
  trackEvent('conversion', 'conversion', {
    conversion_name: conversionName,
    conversion_value: value,
    ...properties,
  });
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    flushEvents();
  });

  // Also flush on visibility change (user switches tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushEvents();
    }
  });
}
